import React, { useEffect, useState } from "react";
import {
  Card,
  Form,
  Button,
  message,
  Spin,
  Input,
  Select,
  Typography,
  Space,
  Row,
  Col,
  Modal,
} from "antd";
import { MailOutlined, EditOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { supabase } from "../../lib/supabase";
import { useCompany } from "../../hooks/useCompany";
import { useUser } from "../../hooks/useUser";

const { Text } = Typography;

interface PricingPaymentTerm {
  id: number;
  days: number;
  interest_vat_import_duties: number;
  created_at: string;
}

// Extend the base company type from useCompany
declare module "../../hooks/useCompany" {
  interface Company {
    pricing_payment_term?: number;
    invoice_email?: string;
  }
}

export const PaymentTermsSettings = () => {
  const { t } = useTranslation();
  const {
    selectedCompany,
    companyDetails,
    companyDetailsLoading,
    refetchCompanyDetails,
  } = useCompany();
  const { user } = useUser();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [paymentTerms, setPaymentTerms] = useState<PricingPaymentTerm[]>([]);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, [selectedCompany]);

  // Prefill invoice email with user email if not set
  useEffect(() => {
    if (
      !companyDetails?.invoice_email &&
      user?.email &&
      form.getFieldValue("invoice_email") !== user.email
    ) {
      form.setFieldsValue({ invoice_email: user.email });
    }
  }, [companyDetails?.invoice_email, user?.email, form]);

  const fetchSettings = async () => {
    if (!selectedCompany) {
      setLoading(false);
      return;
    }

    try {
      // Fetch all available payment terms
      const { data: allPaymentTerms, error: paymentTermsError } = await supabase
        .from("pricing_payment_terms")
        .select("*")
        .order("days", { ascending: true });

      if (paymentTermsError) throw paymentTermsError;
      setPaymentTerms(allPaymentTerms || []);

      // Prefer company setting; otherwise preselect Net 30 for invoicing default.
      const defaultTermId =
        companyDetails?.pricing_payment_term ||
        allPaymentTerms?.find((term) => term.days === 30)?.id ||
        allPaymentTerms?.[0]?.id;
      form.setFieldsValue({
        pricing_payment_term: defaultTermId,
        invoice_email: companyDetails?.invoice_email || "",
      });
    } catch (error) {
      console.error("Error fetching settings:", error);
      message.error(t("paymentTerms.fetchError"));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: {
    pricing_payment_term: number;
    invoice_email: string;
  }) => {
    if (!selectedCompany) return;

    Modal.confirm({
      title: t("paymentTerms.settingsUpdateInfoTitle"),
      content: t("paymentTerms.settingsUpdateInfoMessage"),
      okText: t("paymentTerms.settingsUpdateInfoOk"),
      cancelText: t("common.cancel"),
      onOk: async () => {
        setSaving(true);
        try {
          const { error: updateError } = await supabase
            .from("company")
            .update({
              pricing_payment_term: values.pricing_payment_term,
              invoice_email: values.invoice_email,
            })
            .eq("id", selectedCompany);

          if (updateError) throw updateError;

          await refetchCompanyDetails();
          message.success(t("paymentTerms.saveSuccess"));
          setIsEditing(false);
        } catch (error) {
          console.error("Error saving settings:", error);
          message.error(t("paymentTerms.saveError"));
        } finally {
          setSaving(false);
        }
      },
    });
  };

  if (loading || companyDetailsLoading) {
    return (
      <Card>
        <div style={{ textAlign: "center", padding: "20px" }}>
          <Spin />
        </div>
      </Card>
    );
  }

  const currentPaymentTerm = paymentTerms.find(
    (term) => term.id === companyDetails?.pricing_payment_term
  );

  // Display fallback when company has no term: Net 30 (same as invoice/Moneybird default).
  const defaultPaymentTerm =
    paymentTerms.find((term) => term.days === 30) || paymentTerms[0];
  const displayPaymentTerm = currentPaymentTerm || defaultPaymentTerm;

  return (
    <Card size="small">
      {!isEditing ? (
        <Row gutter={24} align="middle">
          <Col span={11}>
            <Space direction="vertical" size={0} style={{ width: "100%" }}>
              <Text type="secondary" style={{ fontSize: "12px" }}>
                {t("paymentTerms.invoiceEmail")}
              </Text>
              <Text strong style={{ fontSize: "14px" }}>
                {companyDetails?.invoice_email || "-"}
              </Text>
            </Space>
          </Col>
          <Col span={11}>
            <Space direction="vertical" size={0} style={{ width: "100%" }}>
              <Text type="secondary" style={{ fontSize: "12px" }}>
                {t("paymentTerms.paymentTermSection")}
              </Text>
              <Text strong style={{ fontSize: "14px" }}>
                {displayPaymentTerm
                  ? `${displayPaymentTerm.days} ${t(
                      displayPaymentTerm.days === 1
                        ? "paymentTerms.dayLabel"
                        : "paymentTerms.daysLabel"
                    )}, ${displayPaymentTerm.interest_vat_import_duties}% ${t(
                      "paymentTerms.interestLabel"
                    )}`
                  : "-"}
              </Text>
            </Space>
          </Col>
          <Col span={2} style={{ textAlign: "right" }}>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => {
                form.setFieldsValue({
                  invoice_email:
                    companyDetails?.invoice_email || user?.email || "",
                  pricing_payment_term:
                    companyDetails?.pricing_payment_term ||
                    paymentTerms.find((term) => term.days === 30)?.id ||
                    paymentTerms[0]?.id,
                });
                setIsEditing(true);
              }}
            />
          </Col>
        </Row>
      ) : (
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="invoice_email"
                label={t("paymentTerms.invoiceEmail")}
                rules={[
                  { type: "email", message: t("paymentTerms.invalidEmail") },
                ]}
              >
                <Input
                  prefix={<MailOutlined />}
                  placeholder={t("paymentTerms.invoiceEmailPlaceholder")}
                  allowClear
                />
              </Form.Item>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t("paymentTerms.invoiceEmailHelpText")}
              </Typography.Text>
            </Col>
            <Col span={12}>
              <Form.Item
                name="pricing_payment_term"
                label={t("paymentTerms.paymentTermSection")}
              >
                <Select placeholder={t("paymentTerms.selectTermPlaceholder")}>
                  {paymentTerms.map((term) => (
                    <Select.Option key={term.id} value={term.id}>
                      {term.days}{" "}
                      {t(
                        term.days === 1
                          ? "paymentTerms.dayLabel"
                          : "paymentTerms.daysLabel"
                      )}
                      , {term.interest_vat_import_duties}%{" "}
                      {t("paymentTerms.interestLabel")}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t("paymentTerms.paymentTermInterestExplanation")}
              </Typography.Text>
            </Col>
          </Row>
          <Row justify="end">
            <Space>
              <Button onClick={() => setIsEditing(false)}>
                {t("common.cancel")}
              </Button>
              <Button type="primary" htmlType="submit" loading={saving}>
                {t("common.save")}
              </Button>
            </Space>
          </Row>
        </Form>
      )}
    </Card>
  );
};
